import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Phone, ArrowRight, Calculator, Building2, Lightbulb, ThermometerSun, Wind, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import MethodologyCallout from '@/components/MethodologyCallout';
import Breadcrumbs from '@/components/Breadcrumbs';

const SBEMExplained = () => {
  const faqItems = [
    {
      question: 'What does SBEM stand for?',
      answer: 'SBEM stands for Simplified Building Energy Model. It is the government-approved calculation methodology used to assess the energy performance of non-domestic buildings in England and Wales, including offices, retail units, warehouses, factories, schools, hotels and all other commercial premises.'
    },
    {
      question: 'When is an SBEM calculation required?',
      answer: 'An SBEM calculation is required for producing Energy Performance Certificates (EPCs) for non-domestic buildings when selling, letting or constructing the property. It is also needed for Building Regulations Part L compliance on new-build commercial projects and for demonstrating MEES compliance on rental properties.'
    },
    {
      question: 'What is the difference between SBEM and SAP?',
      answer: 'SBEM is used for non-domestic (commercial) buildings, while SAP (Standard Assessment Procedure) is used for domestic dwellings. SBEM handles more complex building types with multiple zones, varying occupancy patterns, and diverse heating and cooling systems. SAP is specifically designed for houses and flats.'
    },
    {
      question: 'How long does an SBEM assessment take?',
      answer: 'The site survey typically takes 1-4 hours depending on building size and complexity. The SBEM modelling and certificate production usually takes 5-7 working days after the survey. Larger or more complex buildings such as multi-zone factories or buildings with mixed-use areas may take longer to model accurately.'
    },
    {
      question: 'Who can carry out SBEM calculations?',
      answer: 'SBEM calculations for EPCs must be carried out by accredited Non-Domestic Energy Assessors (NDEAs). For Building Regulations Part L compliance, calculations can be performed by qualified energy consultants using approved SBEM software. Our assessors hold Elmhurst accreditation for non-domestic energy assessment.'
    },
    {
      question: 'Can SBEM modelling help improve my building rating?',
      answer: 'Yes. SBEM modelling allows assessors to test the impact of proposed improvements before committing to expenditure. We can model scenarios such as LED lighting upgrades, heating system replacements, insulation improvements and renewable energy installations to show which measures offer the best return on investment for your specific building.'
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SBEM Calculation and Energy Modelling",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "telephone": "+44-1392-980077",
      "email": "info@energyperformancedirect.co.uk",
      "areaServed": ["Devon", "Cornwall", "Somerset", "Dorset"]
    },
    "description": "Professional SBEM calculations and energy modelling for commercial and industrial buildings across South West England."
  };

  return (
    <>
      <Helmet>
        <title>SBEM Explained | Simplified Building Energy Model Guide | Commercial EPC Calculations</title>
        <meta name="description" content="Complete guide to SBEM (Simplified Building Energy Model) calculations for commercial and industrial buildings. How SBEM works, when it's required, and what affects your non-domestic EPC rating." />
        <meta name="keywords" content="SBEM calculation, SBEM modelling, SBEM model, SBEM energy model, SBEM assessment, commercial SBEM calculation, industrial SBEM calculation, warehouse SBEM calculation, factory SBEM calculation, commercial building SBEM modelling, SBEM compliance modelling, SBEM consultant" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/sbem-explained" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <Navbar />
      <Breadcrumbs items={[{ label: 'Resources', path: '/resources' }, { label: 'SBEM Explained', path: '/sbem-explained' }]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 text-white py-16" data-testid="sbem-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm mb-6">
              <Calculator className="w-4 h-4" />
              Knowledge Hub
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              SBEM Explained: How Commercial EPC Ratings Are Calculated
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
              A practical guide to the Simplified Building Energy Model used for warehouses, factories, offices, retail units and all non-domestic buildings in England and Wales.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700">
                <Link to="/booking?service=commercial_warehouse">
                  Get SBEM Assessment Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 01392 980077
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is SBEM */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What is SBEM?</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                SBEM (Simplified Building Energy Model) is the government-approved software tool used to calculate the energy performance of non-domestic buildings in England and Wales. Developed by BRE (Building Research Establishment) on behalf of the Department for Energy Security and Net Zero, SBEM produces the standardised energy ratings that appear on commercial Energy Performance Certificates.
              </p>
              <p>
                When you receive a non-domestic EPC for your warehouse, factory, office or retail premises, the rating has been calculated using SBEM methodology. The software models the building as a series of zones, each with defined construction, heating, cooling, lighting and ventilation characteristics. It then calculates annual energy consumption and carbon dioxide emissions, comparing these against a notional reference building of the same size and shape to produce the familiar A-to-G rating scale.
              </p>
              <p>
                Unlike domestic SAP assessments which focus on houses and flats, SBEM handles the full complexity of commercial buildings. A single warehouse with office annexe, loading bays, staff areas and mezzanine storage presents multiple zones with different occupancy patterns, heating requirements and lighting specifications. SBEM models each zone independently before calculating the whole-building performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How SBEM Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Does SBEM Assess?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Building2, title: 'Building Fabric', desc: 'Wall construction, roof insulation, floor type, glazing specifications, air permeability and thermal bridging. These define how well the building retains heat.' },
                { icon: ThermometerSun, title: 'Heating Systems', desc: 'Boilers, radiant panels, warm air heaters, heat pumps, underfloor heating and their associated controls. Efficiency and fuel type directly affect ratings.' },
                { icon: Lightbulb, title: 'Lighting', desc: 'Lamp types (LED, fluorescent, sodium), lighting density, occupancy sensing, daylight dimming and manual switching. Lighting often dominates warehouse energy use.' },
                { icon: Wind, title: 'Ventilation', desc: 'Natural ventilation, mechanical supply and extract, heat recovery units, air handling systems and local extraction for industrial processes.' },
                { icon: Sun, title: 'Renewable Energy', desc: 'Solar PV panels, biomass boilers, heat pumps, solar thermal systems and wind turbines. These reduce the building\'s net energy consumption.' },
                { icon: Calculator, title: 'Hot Water', desc: 'Domestic hot water systems for washrooms, kitchens and staff facilities. System type, insulation and controls all contribute to the assessment.' }
              ].map((item, i) => (
                <Card key={i} className="bg-white">
                  <CardContent className="p-6">
                    <item.icon className="w-10 h-10 text-blue-600 mb-4" />
                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SBEM for Industrial Buildings */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">SBEM for Industrial Buildings</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                Industrial buildings present particular challenges for SBEM modelling. A typical <Link to="/warehouse-epc" className="text-green-600 hover:underline">warehouse</Link> or <Link to="/factory-epc" className="text-green-600 hover:underline">factory</Link> may feature high ceilings exceeding 10 metres, large roller shutter doors, minimal insulation in older structures, and heating systems designed for intermittent use rather than continuous comfort.
              </p>
              <p>
                Our assessors have extensive experience modelling these building types across Devon, Cornwall, Somerset and Dorset. Common industrial SBEM scenarios include:
              </p>
              <ul>
                <li><strong>Warehouse SBEM modelling:</strong> Large open-plan storage areas with high-bay lighting, gas-fired radiant heaters and minimal partition walls. The ratio of floor area to envelope area, combined with limited insulation, typically produces ratings between D and F.</li>
                <li><strong>Factory SBEM modelling:</strong> Production halls with process ventilation, multiple heating zones and a mix of natural and artificial lighting. Ancillary offices and staff areas require separate zone definitions within the model.</li>
                <li><strong>Workshop SBEM modelling:</strong> Smaller <Link to="/workshop-epc" className="text-green-600 hover:underline">workshop</Link> units often combine working space with trade counter, office and storage areas. Each area has different activity profiles that the SBEM model must capture accurately.</li>
                <li><strong>Distribution centre SBEM modelling:</strong> Loading dock areas, temperature-controlled zones and high-throughput spaces with frequent door openings require careful consideration of air infiltration rates.</li>
              </ul>
              <p>
                It is worth noting that SBEM assesses the building itself, not the processes carried out inside it. Manufacturing equipment energy use, material handling systems and production-line power consumption are excluded from the calculation. The EPC rating reflects the building envelope, fixed building services (heating, cooling, lighting, ventilation) and any renewable energy generation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SBEM Process */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">The SBEM Assessment Process</h2>
            <div className="space-y-6">
              {[
                { step: '1', title: 'Site Survey', desc: 'Our assessor visits your property to measure dimensions, inspect construction details, record heating and lighting systems, check ventilation arrangements, and photograph key features. For a standard industrial unit, this takes 1-3 hours.' },
                { step: '2', title: 'Data Input and Modelling', desc: 'Survey data is entered into the approved SBEM software. The building is divided into thermal zones based on activity type, heating provision and construction. Each zone is defined with its specific characteristics.' },
                { step: '3', title: 'Energy Calculation', desc: 'SBEM calculates the annual energy demand for heating, cooling, lighting, ventilation and hot water. It then compares your building against a notional reference building of identical geometry but with standardised systems and construction.' },
                { step: '4', title: 'Rating and Certificate', desc: 'The comparison produces a Building Emission Rate (BER) expressed as kgCO2/m2/year. This is converted to the A-to-G rating scale and the EPC is lodged on the national register. You receive the certificate with detailed recommendations for improvement.' }
              ].map((item, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-xl font-bold">{item.step}</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-gray-300">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Improving Ratings */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Using SBEM to Improve Your Rating</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p>
                One of the practical benefits of SBEM modelling is the ability to test proposed improvements before committing expenditure. Our assessors can model the impact of various upgrades on your building's energy rating, helping you make informed decisions about which measures offer the best value.
              </p>
              <p>
                Common improvements that show significant benefit in SBEM calculations for industrial buildings include:
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {[
                { title: 'LED Lighting Upgrade', impact: 'High', desc: 'Replacing high-bay sodium or metal halide lighting with LED equivalents typically improves SBEM ratings by 10-20 points. For warehouses where lighting is the dominant energy use, this can shift a rating from F to D.' },
                { title: 'Roof Insulation', impact: 'Medium-High', desc: 'Adding insulation above existing industrial roofing reduces heat loss significantly. Combined with improved airtightness, this can improve ratings by 5-15 points depending on existing conditions.' },
                { title: 'Heating Controls', impact: 'Medium', desc: 'Adding time and temperature controls, optimum start/stop and zone control to existing heating systems demonstrates better management of energy use in the SBEM model.' },
                { title: 'Solar PV Installation', impact: 'Medium-High', desc: 'Large industrial roof areas are ideal for solar PV. The generated electricity offsets grid consumption in the SBEM calculation, improving the overall rating. Panels can also generate income through export.' }
              ].map((item, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-lg">{item.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${item.impact === 'High' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {item.impact} Impact
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">SBEM Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm" data-testid={`sbem-faq-${index}`}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Guides and Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid="link-industrial-epc">
                <h3 className="font-semibold mb-1">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">Warehouse, factory and workshop assessments</p>
              </Link>
              <Link to="/mees-commercial-buildings" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid="link-mees">
                <h3 className="font-semibold mb-1">MEES Regulations Guide</h3>
                <p className="text-sm text-gray-600">Minimum energy efficiency standards for landlords</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution centres and storage facilities</p>
              </Link>
              <Link to="/factory-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Factory EPCs</h3>
                <p className="text-sm text-gray-600">Manufacturing plants and production facilities</p>
              </Link>
              <Link to="/workshop-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">Workshop EPCs</h3>
                <p className="text-sm text-gray-600">Light industrial units and trade premises</p>
              </Link>
              <Link to="/resources/epc-explained" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <h3 className="font-semibold mb-1">EPC Explained</h3>
                <p className="text-sm text-gray-600">General guide to Energy Performance Certificates</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need an SBEM Assessment?</h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Professional SBEM calculations for commercial and industrial buildings across Devon, Cornwall, Somerset and Dorset. Accredited assessors with fast turnaround.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              <Link to="/booking?service=commercial_warehouse">
                Request Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="tel:01392980077">
                <Phone className="mr-2 w-5 h-5" />
                Call 01392 980077
              </a>
            </Button>
          </div>
        </div>
      </section>

      <MethodologyCallout
        context="For SBEM-based commercial advice engagements,"
        reportCode="NDEAR (Non-Domestic Energy Advice Report)"
      />

      <Footer />
    </>
  );
};

export default SBEMExplained;
